/**
 * <TutorLiveRail> — une colonne du live du masteur, repliée (molecule).
 *
 * Les chevrons de l'ancien focus (`TutorFocusLayoutM`), repris au round 3 (Wajih : leur retrait était
 * une erreur) et de la colonne repliable de la V2 : la pellicule et le fil se replient pour élargir le
 * centre — pendant une présentation surtout, où le centre est ce que voient les élèves. Repliée, une
 * colonne garde l'essentiel en visages : les élèves (le regardé entouré) à gauche, ce que le fil
 * demande à droite (le signe de chaque entrée, l'urgente teintée). Le chevron la rouvre. Les largeurs
 * sont celles de l'ancien focus : 40 à gauche, 80 à droite (`RAIL_WIDTH`).
 *
 * Contrôlée : ce qui est replié vit dans la page, qui le reçoit en props.
 */
import React from "react"
import { Pressable, ScrollView, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { type AvatarSign, SignedAvatar } from "../SignedAvatar"

/** Une ligne repliée : un visage, et ce qui le marque. */
export type TutorLiveRailItem = {
    id: string
    firstname: string
    avatarUrl?: string
    /** Le regardé : entouré de l'univers. */
    selected?: boolean
    /** Un signe posé sur le visage (le signal d'une entrée du fil), et sa pastille. */
    sign?: AvatarSign
    /** Ce que dit la ligne au lecteur d'écran : « Karim lève la main ». */
    label: string
}

export type TutorLiveRailProps = {
    side: "left" | "right"
    items: TutorLiveRailItem[]
    onPressItem: (id: string) => void
    onExpand: () => void
    /** « Déplier les élèves », « Déplier le fil ». */
    expandLabel: string
    testID?: string
}

/** Les largeurs repliées de l'ancien focus (`collapsedLeftWidth`, `collapsedRightWidth`). */
export const RAIL_WIDTH = { left: 40, right: 80 } as const

const FACE = 28

export function TutorLiveRail(props: TutorLiveRailProps) {
    const { side, items, onPressItem, onExpand, expandLabel, testID } = props
    return (
        <View style={styles.root} testID={testID}>
            <IconButton
                iconName={side === "left" ? "panel-left-open" : "panel-right-open"}
                appearance="transparent"
                iconColor="text-muted"
                size={32}
                iconSize={18}
                onPress={onExpand}
                accessibilityLabel={expandLabel}
            />
            <ScrollView contentContainerStyle={styles.list} showsVerticalScrollIndicator={false}>
                {items.map((item) => (
                    <Pressable
                        key={item.id}
                        onPress={() => onPressItem(item.id)}
                        accessibilityRole="button"
                        accessibilityState={{ selected: item.selected === true }}
                        accessibilityLabel={item.label}
                        style={styles.item}
                    >
                        <SignedAvatar
                            uri={item.avatarUrl}
                            name={item.firstname}
                            size={FACE}
                            sign={item.sign}
                            selected={item.selected}
                        />
                    </Pressable>
                ))}
            </ScrollView>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0, alignItems: "center" as const, gap: theme.spacing["2"] },
    list: { alignItems: "center" as const, gap: theme.spacing["2"], paddingVertical: theme.spacing["1"] },
    item: { width: FACE + 6, height: FACE + 6, alignItems: "center" as const, justifyContent: "center" as const },
}))
